/**
 * Branding tokens applied per-tenant at runtime.
 *
 * Each société can override these values. They are injected into the document
 * root as CSS variables consumed by Tailwind through `tailwind.config.ts`.
 *
 * Colors are stored as HSL triplets (e.g. `353 100% 46%`) so that Tailwind
 * opacity modifiers continue to work (`bg-brand/20`, `text-brand/80`, etc.).
 */

export type HslTriplet = `${number} ${number}% ${number}%`;

export interface BrandingTokens {
  /** Public name displayed in chrome and emails ("noaretraite", "Bilan Retraite Acme", ...). */
  productName: string;
  /** Short tagline displayed in hero / nav. */
  productTagline?: string;
  /** Color of the primary brand surface. */
  brand: HslTriplet;
  /** Text color on top of `brand`. */
  brandForeground: HslTriplet;
  /** Hover state of the brand color. */
  brandHover: HslTriplet;
  /** Muted background derived from the brand (used for chips, badges, etc.). */
  brandMuted: HslTriplet;
  /** Accent color (typically a darker neutral). */
  accent: HslTriplet;
  accentForeground: HslTriplet;
  /** Optional logo URLs. */
  logoUrl?: string;
  logoDarkUrl?: string;
  faviconUrl?: string;
  /** Optional CSS font-family stacks. */
  fontSans?: string;
  fontDisplay?: string;
  /** Optional legal footer line override. */
  footerLegal?: string;
}

/**
 * Default brand tokens — extracted from the official AON / NOA charte graphique
 * (palette : AON ROUGE #EB0017, NAVY AON #262836, AON GRIS MOYEN #82939A, AON VERT #12A88A).
 */
export const defaultBranding: BrandingTokens = {
  productName: "noaretraite",
  productTagline: "Le Bilan Retraite Individuel par Aon.",
  brand: "354 100% 46%",
  brandForeground: "0 0% 100%",
  brandHover: "354 100% 40%",
  brandMuted: "354 90% 96%",
  accent: "233 17% 18%",
  accentForeground: "0 0% 100%",
  fontSans:
    '"Helvetica Neue", "Inter", "Helvetica", "Arial", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  fontDisplay:
    '"Helvetica Now Display", "Helvetica Neue", "Inter", "Helvetica", "Arial", system-ui, sans-serif',
  footerLegal: "© 2026 Aon France. 31-35 rue de la Fédération, 75015 Paris.",
};
